import {
    ChangeEvent,
    memo,
    Suspense,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useTransition
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Button from "@atlaskit/button";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import SearchIcon from "@atlaskit/icon/glyph/search";
import { media } from "@atlaskit/primitives/responsive";
import Textfield from "@atlaskit/textfield";

import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";

import {
    TemplateFuzzySearchSuggestions,
    TemplateFuzzySearchSuggestionsLoading
} from "../../components/contentful-relay-components/template-fuzzy-search-suggestions-contentful-relay";
import {
    SearchSuggestion,
    useSearchSuggestionsLoader
} from "../../hooks/contentful-relay-hooks/use-search-suggestions-contentful";

const searchBarContainer = css({
    display: "flex",
    alignItems: "center",
    paddingBlockStart: "34px"
});

const searchBar = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #6B77BC",
    width: "965px",
    height: "57px",
    borderRadius: "7px",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#FFFFFF",
    // Tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    input: {
        justifyContent: "flex-start",
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "24px",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#000000",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        fontFamily: "var(--bxp-charlie-text-font-family)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        [media.below.sm]: {
            fontSize: "0.875rem"
        }
    },
    [media.only.md]: {
        width: "500px"
    },
    [media.only.sm]: {
        width: "500px"
    },
    [media.only.xs]: {
        width: "290px"
    },
    [media.below.xs]: {
        width: "290px"
    }
});

const searchIconStyle = css({
    height: "54px",
    width: "55px",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#808080",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const searchIconResultsStyle = css({
    [media.below.sm]: {
        display: "none"
    }
});

const closeButtonStyles = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#808080",
    border: "none",
    flexShrink: 0,
    cursor: "pointer"
});

const closeIconSectionStyles = css({
    "display": "flex",
    "justifyContent": "flex-end",
    "padding": "0",
    "border": "none",
    "alignItems": "center",
    "marginBlockStart": "8px",
    // Tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& button": {
        "background": "none",
        "&:hover": {
            background: "none"
        },
        "&:active": {
            background: "none"
        }
    }
});

type TemplateFuzzySearchIconProps = {
    variant?: "results";
};

const TemplateFuzzySearchIcon = memo(
    ({ variant }: TemplateFuzzySearchIconProps) => {
        const intl = useIntl();
        return (
            <div
                css={[
                    searchIconStyle,
                    variant == "results" ? searchIconResultsStyle : null
                ]}
            >
                <SearchIcon
                    label={intl.formatMessage(
                        ConfluenceTemplateMessages.searchLabel
                    )}
                    size="medium"
                />
            </div>
        );
    }
);

type TemplateFuzzySearchCloseIconProps = {
    onClick: React.ComponentProps<typeof Button>["onClick"];
};

const TemplateFuzzySearchCloseIcon = memo(
    ({ onClick }: TemplateFuzzySearchCloseIconProps) => {
        const intl = useIntl();
        return (
            <div css={closeIconSectionStyles}>
                <Button
                    id="clear-button"
                    type="submit"
                    color="#fff"
                    css={closeButtonStyles}
                    onClick={onClick}
                    testId="clear-button"
                >
                    <CrossIcon
                        label={intl.formatMessage(
                            ConfluenceTemplateMessages.closeLabel
                        )}
                        primaryColor={"#808080"}
                        size="medium"
                    />
                </Button>
            </div>
        );
    }
);

const SET_INPUT_VALUE = "SET_INPUT_VALUE";
const SET_SUGGESTION = "SET_SUGGESTION";
const ACCEPT_SUGGESTION = "ACCEPT_SUGGESTION";
const HIDE_SUGGESTIONS = "HIDE_SUGGESTIONS";
const SHOW_SUGGESTIONS = "SHOW_SUGGESTIONS";

type TemplateFuzzySearchAction =
    | {
          type: typeof SET_INPUT_VALUE;
          value: string | SearchSuggestion | null;
      }
    | { type: typeof SET_SUGGESTION; value: SearchSuggestion | null }
    | { type: typeof ACCEPT_SUGGESTION; value: SearchSuggestion }
    | { type: typeof HIDE_SUGGESTIONS }
    | { type: typeof SHOW_SUGGESTIONS };

type TemplateFuzzySearchInputState = {
    inputValue: string | SearchSuggestion | null;
    shouldShowSuggestions: boolean;
    currentSuggestion?: SearchSuggestion | null;
};

type TemplateFuzzySearchState = TemplateFuzzySearchInputState & {
    suggestions?: SearchSuggestion[] | null;
};

const reduceSearchState = (
    state: TemplateFuzzySearchInputState,
    action: TemplateFuzzySearchAction
) => {
    switch (action.type) {
        case SET_INPUT_VALUE: {
            if (action.value !== state.inputValue) {
                return {
                    ...state,
                    inputValue: action.value,
                    shouldShowSuggestions: true
                };
            }
            return state;
        }
        case SET_SUGGESTION: {
            if (action.value !== state.currentSuggestion) {
                return {
                    ...state,
                    currentSuggestion: action.value,
                    shouldShowSuggestions: true
                };
            }
            return state;
        }
        case ACCEPT_SUGGESTION: {
            return {
                ...state,
                inputValue: action.value,
                shouldShowSuggestions: false,
                currentSuggestion: null
            };
        }
        case HIDE_SUGGESTIONS: {
            if (state.shouldShowSuggestions) {
                return { ...state, shouldShowSuggestions: false };
            }
            return state;
        }
        case SHOW_SUGGESTIONS: {
            if (!state.shouldShowSuggestions) {
                return { ...state, shouldShowSuggestions: true };
            }
            return state;
        }
        default: {
            const exhaustiveCheck: never = action;
            console.error(`Invalid action type ${exhaustiveCheck}`);
            return state;
        }
    }
};

const toInputDisplay = (searchTerm?: string | SearchSuggestion | null) => {
    if (!searchTerm) return "";
    if (typeof searchTerm === "string") return searchTerm;
    return searchTerm.name ?? "";
};

const useSearchState = (
    initialSearchTerm?: string | SearchSuggestion | null
) => {
    const [state, dispatch] = useReducer(reduceSearchState, {
        inputValue: initialSearchTerm ?? null,
        shouldShowSuggestions: false
    });

    const actions = useMemo(
        () => ({
            setInputValue: (value: string | SearchSuggestion | null) => {
                dispatch({ type: SET_INPUT_VALUE, value });
            },
            acceptSuggestion: (value: SearchSuggestion) => {
                dispatch({ type: ACCEPT_SUGGESTION, value });
            },
            setSuggestion: (value: SearchSuggestion | null) => {
                dispatch({ type: SET_SUGGESTION, value });
            },
            hideSuggestions: () => {
                dispatch({ type: HIDE_SUGGESTIONS });
            },
            showSuggestions: () => {
                dispatch({ type: SHOW_SUGGESTIONS });
            }
        }),
        []
    );

    return useMemo<[TemplateFuzzySearchState, typeof actions]>(
        () => [state, actions],
        [state, actions]
    );
};

export type TemplateFuzzySearchProps = {
    value?: string | SearchSuggestion | null;
    onSubmit?: (value: string | SearchSuggestion | null) => void;
};

export const TemplateFuzzySearch = ({
    value,
    onSubmit
}: TemplateFuzzySearchProps) => {
    const intl = useIntl();
    const [
        { inputValue, currentSuggestion, shouldShowSuggestions },
        {
            setInputValue,
            setSuggestion,
            acceptSuggestion,
            hideSuggestions,
            showSuggestions
        }
    ] = useSearchState(value);

    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const hasSentAnalytic = useRef(false);
    const onSubmitRef = useRef(onSubmit);

    onSubmitRef.current = onSubmit;

    const [suggestions, loadSuggestionsQuery] = useSearchSuggestionsLoader();

    const [, startLoadSuggestions] = useTransition();

    // Hide suggestions when the search term changes.
    useEffect(hideSuggestions, [hideSuggestions, value]);

    useEffect(() => {
        if (!shouldShowSuggestions) return;

        //Handling clicking outside the suggestion list, so it automatically closes when not the focus
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node)
            ) {
                hideSuggestions();
            }
        };

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === "Escape") hideSuggestions();
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEsc, false);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEsc, false);
        };
    }, [hideSuggestions, shouldShowSuggestions]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();

        // Send analytics data only the first time
        if (!hasSentAnalytic.current) {
            ConfluenceTemplateAnalytics.sendTemplateFuzzySearchAnalytic();
            hasSentAnalytic.current = true; // Set the flag to true after the first call
        }
        setInputValue(event?.target?.value);
        if (event?.target?.value.length === 0) {
            hideSuggestions();
        }
    };

    const handleInputFocus = () => {
        if (!suggestions) startLoadSuggestions(loadSuggestionsQuery);
        ConfluenceTemplateAnalytics.sendTemplateFuzzySearchFocusAnalytic();
        showSuggestions();
    };

    const handleMouseEnter = () => {
        if (!suggestions) startLoadSuggestions(loadSuggestionsQuery);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputValue) {
                onSubmitRef.current?.(inputValue);
            }
        }
    };

    const handleSuggestionClick = (suggestion: SearchSuggestion) => {
        acceptSuggestion(suggestion);
        if (suggestion) {
            onSubmitRef.current?.(suggestion);
        }
    };

    return (
        <div css={searchBarContainer}>
            <div ref={searchRef} css={searchBar}>
                <Textfield
                    ref={inputRef}
                    id="templates-fuzzy-search-input"
                    testId="templates-fuzzy-search-input"
                    placeholder={intl.formatMessage(
                        ConfluenceTemplateMessages.fuzzySearchInputText
                    )}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onMouseEnter={handleMouseEnter}
                    onKeyDown={handleKeyDown}
                    value={toInputDisplay(inputValue)}
                    autoComplete="off"
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-owns="suggestion-box"
                    aria-expanded={shouldShowSuggestions}
                    aria-activedescendant={
                        currentSuggestion
                            ? `option-${currentSuggestion.id}`
                            : undefined
                    }
                    elemBeforeInput={<TemplateFuzzySearchIcon />}
                    elemAfterInput={
                        inputValue ? (
                            <TemplateFuzzySearchCloseIcon
                                onClick={() => setInputValue(null)}
                            />
                        ) : null
                    }
                />
                {shouldShowSuggestions && suggestions && (
                    <ContentNotFoundErrorBoundary fallback={<div />}>
                        <Suspense
                            fallback={
                                inputValue && (
                                    <TemplateFuzzySearchSuggestionsLoading />
                                )
                            }
                        >
                            <TemplateFuzzySearchSuggestions
                                queryRefSuggestions={suggestions}
                                currentSuggestion={currentSuggestion}
                                inputValue={inputValue}
                                onSelect={setSuggestion}
                                onSubmit={handleSuggestionClick}
                            />
                        </Suspense>
                    </ContentNotFoundErrorBoundary>
                )}
            </div>
        </div>
    );
};
